/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useRef } from "react";
import {
  mergeProps,
  useFocusRing,
  useHover,
  useId,
  useRadio,
  useRadioGroup,
} from "react-aria";
import type { RadioGroupProps, RadioGroupState } from "react-stately";
import { useRadioGroupState as useRadioGroupStateBase } from "react-stately";

import { useLocale } from "@superweb/intl";
import { cssFns } from "@superweb/css";
import {
  ExperimentalFormGroup,
  ExperimentalFormRow,
  icons,
  TextField,
  useDebouncedState,
  useTypo,
  useUiColors,
  type ComboBoxOption,
  type ComboBoxState,
  type RadioGroupState as RadioGroupStateUi,
  type TextFieldState,
} from "@superweb/ui";

import { RowWithScroll } from "@fleet/ui";
import {
  useOnDemandV1CarBrandsGet,
  useOnDemandV1CarColorsGet,
  useOnDemandV1CarModelsGet,
  useOnDemandV1CarYearsGet,
} from "@fleet/api/fleet-forms";

import { useMessage } from "#internal/intl";
import { FormSuggest } from "#internal/ui";
import { useErrorMessages, useValidate } from "#internal/utils";

type GroupState = {
  value?: "car" | "bike" | "rickshaw";
  errorMessage?: string;
  errorVisible?: boolean;
};

export type VehicleFormState = {
  number: TextFieldState;
  vehicle_type: RadioGroupStateUi<"car" | "bike" | "rickshaw">;
  brand: ComboBoxState<ComboBoxOption>;
  model: ComboBoxState<ComboBoxOption>;
  color_code: ComboBoxState<ComboBoxOption>;
  year: ComboBoxState<ComboBoxOption>;
};

const Group = ({
  options,
  state,
  onChange,
}: {
  options: { value: string; label: string }[];
  state: GroupState;
  onChange: (state: GroupState) => void;
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();

  const groupProps: RadioGroupProps = {
    errorMessage: state.errorMessage,
    value: state.value ?? null,
    validationState:
      state.errorVisible && state.errorMessage ? "invalid" : "valid",
    orientation: "horizontal",
    onChange: (key) => {
      onChange({
        ...state,
        value: key as "car" | "bike" | "rickshaw",
        errorVisible: false,
        errorMessage: undefined,
      });
    },
  };

  const groupState = useRadioGroupStateBase(groupProps);

  const { radioGroupProps, errorMessageProps } = useRadioGroup(
    groupProps,
    groupState,
  );

  return (
    <div {...radioGroupProps}>
      <RowWithScroll disabledShadow>
        <div css={{ display: "flex", columnGap: "16px" }}>
          {options.map(({ value, label }) => (
            <GroupRadio
              key={value}
              value={value}
              label={label}
              state={groupState}
            />
          ))}
        </div>
      </RowWithScroll>
      {state.errorMessage && state.errorVisible && (
        <div
          css={{
            ...cssFns.padding("0", "16px"),
            paddingBlockStart: "4px",
            ...typo({
              level: "caption1",
              density: "tight",
              weight: "regular",
            }),
            color: uiColors.error,
          }}
          {...errorMessageProps}
        >
          {state.errorMessage}
        </div>
      )}
    </div>
  );
};

const GroupRadio = ({
  value,
  label,
  state,
}: {
  value: string;
  label: string;
  state: RadioGroupState;
}) => {
  const uiColors = useUiColors();
  const typo = useTypo();
  const { focusProps, isFocusVisible: isFocused } = useFocusRing();
  const { hoverProps, isHovered } = useHover({});

  const descriptionId = useId();

  const inputRef = useRef<HTMLInputElement>(null);
  const { inputProps, isSelected } = useRadio(
    { value, "aria-label": label, "aria-describedby": descriptionId },
    state,
    inputRef,
  );

  const radioIcons = () => {
    switch (value) {
      case "car":
        return <icons.CarFill />;
      case "bike":
        return <icons.MotorcycleProfile />;
      case "rickshaw":
        return <icons.TucktuckProfile />;
      default:
        return <div></div>;
    }
  };

  const backgroundColor =
    isSelected || isHovered ? uiColors.controlMinor : uiColors.background;

  return (
    <label
      {...hoverProps}
      css={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: `0 0 0 0 transparent, ${
          isFocused
            ? "inset 0 0 0 2px " + uiColors.focus
            : "0 0 0 0 transparent"
        }`,
        cursor: "pointer",
        width: "172px",
        height: "112px",
        backgroundColor,
        outlineStyle: "none",
        ...cssFns.border({
          style: "solid",
          radius: "16px",
          width: "2px",
          color: isSelected ? "transparent" : uiColors.controlMinor,
        }),
        boxSizing: "border-box",
        ...cssFns.padding("14px"),
        paddingBlockEnd: "18px",
        color: uiColors.text,
        ...typo({ level: "body2", weight: "medium", density: "tight" }),
      }}
    >
      {radioIcons()}
      <span id={descriptionId}>{label}</span>
      <input
        {...mergeProps(inputProps, focusProps)}
        css={{
          width: "100%",
          height: "100%",
          left: "0",
          top: "0",
          position: "absolute",
          opacity: "0",
          cursor: "pointer",
          ...cssFns.margin("0"),
        }}
        ref={inputRef}
      />
    </label>
  );
};

export const VehicleDetailsFields = ({
  state,
  onChangePartial,
  refId,
  vehicleTypes,
  enableVehicleTypesSelection,
}: {
  state: VehicleFormState;
  onChangePartial: (partialNextState: Partial<VehicleFormState>) => void;
  refId: string;
  vehicleTypes: { id: "car" | "bike" | "rickshaw"; name: string }[];
  enableVehicleTypesSelection?: boolean;
}) => {
  const message = useMessage();
  const locale = useLocale();
  const errorMessages = useErrorMessages();
  const { validateTextField, validateComboboxField } = useValidate();

  const vehicleTypeOptions: {
    value: "car" | "bike" | "rickshaw";
    label: string;
  }[] = vehicleTypes.map(({ id, name }) => {
    return { value: id, label: name };
  });

  const debouncedVehicleBrandState = useDebouncedState(state.brand, 300);

  const { data: brandData } = useOnDemandV1CarBrandsGet(
    {
      query: {
        text_query: debouncedVehicleBrandState.inputValue,
        ref_id: refId,
        vehicle_type: state.vehicle_type.value,
      },
      headers: { language: locale.toString() },
    },
    {
      enabled: !!state.vehicle_type.value,
    },
  );

  const vehicleBrandsOptions = brandData?.ok
    ? brandData.data.brands.map((item) => ({ label: item, key: item }))
    : [];

  const debouncedVehicleModelInput = useDebouncedState(
    state.model.inputValue,
    300,
  );

  const { data: modelData } = useOnDemandV1CarModelsGet(
    {
      query: {
        brand: String(debouncedVehicleBrandState.value?.key),
        text_query: debouncedVehicleModelInput,
        ref_id: refId,
        vehicle_type: state.vehicle_type.value,
      },
      headers: { language: locale.toString() },
    },
    {
      enabled: !!debouncedVehicleBrandState.value && !!state.vehicle_type.value,
    },
  );

  const vehicleModelsOptions = modelData?.ok
    ? modelData.data.models.map((item) => ({ label: item, key: item }))
    : [];

  const { data: colorData } = useOnDemandV1CarColorsGet({
    headers: { language: locale.toString() },
  });

  const vehicleColorOptions = colorData?.ok
    ? colorData.data.colors.map(({ name, hex_code }) => ({
        label: name,
        key: hex_code,
      }))
    : [];

  const { data: yearData } = useOnDemandV1CarYearsGet({
    headers: { language: locale.toString() },
  });

  const vehicleYearsOptions = yearData?.ok
    ? yearData.data.years.map((item) => ({
        label: String(item),
        key: String(item),
      }))
    : [];

  return (
    <>
      <TextField
        required
        label={message({
          id: "41652579-a5a6-45f4-a3ce-2e9ca03078a4",
          context: "Yango forms. Vehicle number field",
          default: "License plate number",
        })}
        state={state.number}
        onChange={(newState) => {
          const errorMessage = validateTextField(newState.value);
          onChangePartial({
            number: {
              value: newState.value.toUpperCase(),
              errorMessage,
              errorVisible: !!errorMessage,
            },
          });
        }}
      />
      {vehicleTypeOptions.length > 1 && enableVehicleTypesSelection && (
        <Group
          state={state.vehicle_type}
          onChange={(newState) => {
            onChangePartial({
              vehicle_type: newState,
              brand: {
                value: undefined,
                inputValue: "",
                errorMessage: errorMessages.requiredField,
              },
              model: {
                value: undefined,
                inputValue: "",
                errorMessage: errorMessages.requiredField,
              },
            });
          }}
          options={vehicleTypeOptions}
        />
      )}
      {state.vehicle_type.value && (
        <ExperimentalFormGroup columnsCount={1}>
          <FormSuggest
            required
            data={vehicleBrandsOptions}
            label={message({
              id: "0e684bb9-b11f-498a-8f0c-a73a6da9523d",
              context: "Yango forms. Vehicle brand field",
              default: "Make",
            })}
            state={state.brand}
            onChange={(newState) => {
              const errorMessage = validateComboboxField(newState);
              onChangePartial({
                brand: {
                  ...newState,
                  errorMessage,
                },
                model: {
                  value:
                    state.vehicle_type.value === "car"
                      ? undefined
                      : vehicleModelsOptions[0],
                  inputValue: "",
                  errorMessage:
                    state.vehicle_type.value === "car"
                      ? errorMessages.requiredField
                      : undefined,
                },
              });
            }}
          />
          {state.vehicle_type.value === "car" && (
            <FormSuggest
              required
              data={vehicleModelsOptions}
              label={message({
                id: "0e684bb9-b11f-498a-8f0c-a73a6da9522d",
                context: "Yango forms. Vehicle model field",
                default: "Model",
              })}
              state={state.model}
              onChange={(newState) => {
                const errorMessage = validateComboboxField(newState);
                onChangePartial({
                  model: {
                    ...newState,
                    errorMessage,
                    errorVisible: Boolean(errorMessage),
                  },
                });
              }}
              disabled={!state.brand.value}
            />
          )}
          <ExperimentalFormRow columnsCount={2}>
            <FormSuggest
              required
              data={vehicleColorOptions}
              label={message({
                id: "25636b9c-84b9-44de-b602-5cb0272fef39",
                context: "Yango forms. Vehicle color field",
                default: "Color",
              })}
              state={state.color_code}
              onChange={(newState) => {
                const errorMessage = validateComboboxField(newState);
                onChangePartial({
                  color_code: {
                    ...newState,
                    errorMessage,
                    errorVisible: Boolean(errorMessage),
                  },
                });
              }}
            />
            <FormSuggest
              required
              data={vehicleYearsOptions}
              label={message({
                id: "1194351e-cc7a-4143-9004-2d300c506a72",
                context: "Yango forms. Vehicle year field",
                default: "Year",
              })}
              state={state.year}
              onChange={(newState) => {
                const errorMessage = validateComboboxField(newState);
                onChangePartial({
                  year: {
                    ...newState,
                    errorMessage,
                    errorVisible: Boolean(errorMessage),
                  },
                });
              }}
            />
          </ExperimentalFormRow>
        </ExperimentalFormGroup>
      )}
    </>
  );
};
